import * as vp from 'vopay';
import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '@/services/api.config';
import { useDataFetchIndicator } from '..';

import useTransactionApprovalAccess from '@/hooks/useTransactionApprovalAccess';

type HookProps = {
  api: vp.Api;
  bearer?: string;
};

type ReturnTuple = [
  vp.ApprovalInboxTransactionsResponse | undefined,
  (requestParams: vp.ApprovalInboxTransactionsRequest) => void,
  () => void,
  (
    requestParams: vp.ApprovalInboxTransactionsRequest,
    bearer?: string,
  ) => Promise<vp.ApprovalInboxTransactionsResponse>,
];

function useFetchApprovalTransactionsInbox({ api, bearer }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<vp.ApprovalInboxTransactionsRequest>();
  const [response, setResponse] = useState<vp.ApprovalInboxTransactionsResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  const canUseTransactionApproval = useTransactionApprovalAccess();

  useEffect(() => {
    if (!canUseTransactionApproval) {
      shouldSendRequest && setSendRequest(false);

      return;
    }

    const sendRequest = async (requestParams: vp.ApprovalInboxTransactionsRequest) => {
      setDataFetchingInfo(true);

      const res = await api.fetchApprovalTransactionsInbox(requestParams, bearer, source);

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: vp.ApprovalInboxTransactionsRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
    async (requestParams: vp.ApprovalInboxTransactionsRequest, callBearer?: string) => {
      try {
        setDataFetchingInfo(true);
        const res = await api.fetchApprovalTransactionsInbox(requestParams, callBearer ?? bearer, source);
        setResponse(res);

        return res;
      } finally {
        setDataFetchingInfo(false);
      }
    },
  ];
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(useFetchApprovalTransactionsInbox);
